import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, CheckCircle, XCircle, AlertTriangle, Clock, 
  Users, Plus, X, Pencil, Trash2, Shield, FileText,
  UserPlus, Check, Activity, TrendingUp, MapPin
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentPointages, setRecentPointages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Admin management state
  const [admins, setAdmins] = useState([]);
  const [myPermissions, setMyPermissions] = useState(null);
  const [prysmaUsers, setPrysmaUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [permissions, setPermissions] = useState({
    can_edit_schedules: true,
    can_create_reports: true,
    can_receive_alerts: true,
    can_manage_maintenance: false,
    can_manage_admins: false,
    can_manage_email_digests: false
  });
  
  const navigate = useNavigate();
  const isDark = document.documentElement.classList.contains('dark');
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }
    
    fetchData();
  }, [navigate]);
  
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      const [statsRes, pointagesRes, adminsRes, myPermsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, config),
        axios.get(`${API}/pointages`, config),
        axios.get(`${API}/admins`, config),
        axios.get(`${API}/admins/my-permissions`, config)
      ]);
      
      setStats(statsRes.data);
      setRecentPointages(pointagesRes.data.slice(0, 8));
      setAdmins(adminsRes.data);
      setMyPermissions(myPermsRes.data?.permissions || {});
    } catch (error) {
      toast.error('Erreur de chargement');
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPrysmaUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admins/prysma-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrysmaUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    }
  };
  
  const openAddModal = () => {
    fetchPrysmaUsers();
    setSelectedUser(null);
    setPermissions({
      can_edit_schedules: true,
      can_create_reports: true,
      can_receive_alerts: true,
      can_manage_maintenance: false,
      can_manage_admins: false,
      can_manage_email_digests: false
    });
    setSearchQuery('');
    setShowAddModal(true);
  };
  
  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setPermissions(admin.permissions || {
      can_edit_schedules: true,
      can_create_reports: true,
      can_receive_alerts: true,
      can_manage_maintenance: false,
      can_manage_admins: false,
      can_manage_email_digests: false
    });
    setShowEditModal(true);
  };
  
  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setShowDeleteModal(true);
  };
  
  const addAdmin = async () => {
    if (!selectedUser) {
      toast.error('Veuillez sélectionner un utilisateur');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admins`, {
        username: selectedUser.username,
        permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Administrateur ajouté');
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };
  
  const updateAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admins/${selectedAdmin.id}`, {
        permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Permissions mises à jour');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };
  
  const deleteAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admins/${selectedAdmin.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Administrateur supprimé');
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };
  
  const filteredUsers = prysmaUsers.filter(user => {
    const isAlreadyAdmin = admins.some(a => a.username === user.username);
    const matchesSearch = !searchQuery || 
      user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchQuery.toLowerCase());
    return !isAlreadyAdmin && matchesSearch;
  });
  
  const canManageAdmins = myPermissions?.can_manage_admins;
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5BA4CF]"></div>
        </div>
      </AdminLayout>
    );
  }
  
  const PermissionCheckbox = ({ label, description, checked, onChange, disabled }) => (
    <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
      checked ? 'bg-[#5BA4CF]/10' : 'bg-gray-50 dark:bg-zinc-800'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#5BA4CF]/5'}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-1 w-4 h-4 text-[#5BA4CF] rounded border-gray-300 focus:ring-[#5BA4CF]"
      />
      <div>
        <p className="font-medium text-gray-900 dark:text-white text-sm">{label}</p>
        <p className="text-xs text-gray-500 dark:text-zinc-400">{description}</p>
      </div>
    </label>
  );
  
  // Stats for the header cards
  const todayStats = {
    total: stats?.today_pointages || 0,
    valid: stats?.valid_pointages || 0,
    absent: stats?.absences || 0
  };
  
  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-gray-600 dark:text-zinc-400 mt-1">
            Bienvenue sur votre espace de gestion
          </p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-5 shadow-sm border"
            style={{ 
              background: isDark ? '#1f2937' : '#ffffff',
              borderColor: isDark ? '#374151' : '#e5e7eb'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-zinc-400">Scans aujourd'hui</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{todayStats.total}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#5BA4CF]/10 flex items-center justify-center">
                <Activity className="h-6 w-6 text-[#5BA4CF]" />
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl p-5 shadow-sm border"
            style={{ 
              background: isDark ? '#1f2937' : '#ffffff',
              borderColor: isDark ? '#374151' : '#e5e7eb'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-zinc-400">Validés</p>
                <p className="text-3xl font-bold text-green-500 mt-1">{todayStats.valid}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl p-5 shadow-sm border"
            style={{ 
              background: isDark ? '#1f2937' : '#ffffff',
              borderColor: todayStats.absent > 0 ? '#ef444450' : (isDark ? '#374151' : '#e5e7eb')
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-zinc-400">Absences</p>
                <p className={`text-3xl font-bold mt-1 ${todayStats.absent > 0 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                  {todayStats.absent}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                todayStats.absent > 0 ? 'bg-red-500/10' : 'bg-gray-100 dark:bg-zinc-800'
              }`}>
                <AlertTriangle className={`h-6 w-6 ${todayStats.absent > 0 ? 'text-red-500' : 'text-gray-400'}`} />
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Scans récents */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl shadow-sm border overflow-hidden"
            style={{ 
              background: isDark ? '#1f2937' : '#ffffff',
              borderColor: isDark ? '#374151' : '#e5e7eb'
            }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#5BA4CF]/10 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-[#5BA4CF]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Scans récents
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                      {recentPointages.length} derniers scans
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/plannings')}
                  className="text-[#5BA4CF] hover:bg-[#5BA4CF]/10"
                >
                  Voir tout
                </Button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 max-h-[500px] overflow-y-auto">
              {recentPointages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                    <Clock className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 dark:text-zinc-400 font-medium">Aucun scan aujourd'hui</p>
                  <p className="text-sm text-gray-400 dark:text-zinc-500 mt-1">Les scans apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentPointages.map((pointage, index) => (
                    <motion.div
                      key={pointage.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-4 p-3 rounded-lg transition-all hover:scale-[1.02]"
                      style={{ 
                        background: pointage.status === 'valid' 
                          ? (isDark ? '#22c55e10' : '#f0fdf4')
                          : (isDark ? '#ef444410' : '#fef2f2'),
                        border: `1px solid ${pointage.status === 'valid' ? '#22c55e30' : '#ef444430'}`
                      }}
                    >
                      {/* Status indicator */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        pointage.status === 'valid' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {pointage.status === 'valid' ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                          {pointage.prenom} {pointage.nom}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          <p className="text-xs text-gray-500 dark:text-zinc-400 truncate">
                            {pointage.site_nom}
                          </p>
                        </div>
                      </div>
                      
                      {/* Time & Date */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {new Date(pointage.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-zinc-400">
                          {new Date(pointage.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-zinc-500">
                          {pointage.scan_type === 'fermeture' ? 'Fermeture' : 'Ouverture'}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
          
          {/* Right Column - Administrateurs */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl shadow-sm border overflow-hidden"
            style={{ 
              background: isDark ? '#1f2937' : '#ffffff',
              borderColor: isDark ? '#374151' : '#e5e7eb'
            }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-purple-500" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Administrateurs
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-zinc-400">
                      {admins.length} administrateur{admins.length > 1 ? 's' : ''} configuré{admins.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                {canManageAdmins && (
                  <Button 
                    onClick={openAddModal}
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    <UserPlus className="h-4 w-4 mr-1" />
                    Ajouter
                  </Button>
                )}
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 max-h-[500px] overflow-y-auto">
              <div className="space-y-3">
                {admins.map((admin, index) => (
                  <motion.div
                    key={admin.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-4 p-4 rounded-lg transition-all"
                    style={{ 
                      background: isDark ? '#374151' : '#f9fafb',
                      border: `1px solid ${isDark ? '#4b5563' : '#e5e7eb'}`
                    }}
                  >
                    {/* Avatar */}
                    {admin.photo ? (
                      <img 
                        src={admin.photo} 
                        alt={admin.display_name} 
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-purple-500/20" 
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <Users className="h-6 w-6 text-purple-500" />
                      </div>
                    )}
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white truncate">
                        {admin.display_name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-zinc-400 truncate">
                        {admin.position || admin.username}
                      </p>
                      {/* Permission badges */}
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {admin.permissions?.can_edit_schedules && (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            Horaires
                          </span>
                        )}
                        {admin.permissions?.can_create_reports && (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            Rapports
                          </span>
                        )}
                        {admin.permissions?.can_manage_maintenance && (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                            Maintenance
                          </span>
                        )}
                        {admin.permissions?.can_manage_admins && (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                            Admin
                          </span>
                        )}
                        {admin.permissions?.can_manage_email_digests && (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300">
                            Récaps e-mail
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    {canManageAdmins && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openEditModal(admin)}
                          className="h-8 w-8 text-gray-500 hover:text-[#5BA4CF] hover:bg-[#5BA4CF]/10"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openDeleteModal(admin)}
                          className="h-8 w-8 text-gray-500 hover:text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl"
            style={{ background: isDark ? '#1f2937' : '#ffffff' }}
          >
            <div className="sticky top-0 p-6 border-b flex items-center justify-between" style={{ 
              background: isDark ? '#1f2937' : '#ffffff',
              borderColor: isDark ? '#374151' : '#e5e7eb' 
            }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-purple-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Ajouter un administrateur</h3>
              </div>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* User Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
                  Sélectionner un utilisateur
                </label>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 px-4 rounded-lg border text-gray-900 dark:text-white mb-3"
                  style={{
                    background: isDark ? '#374151' : '#f8f9fa',
                    borderColor: isDark ? '#4b5563' : '#e5e7eb'
                  }}
                />
                <div className="max-h-48 overflow-y-auto rounded-lg border" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
                  {filteredUsers.map(user => (
                    <button
                      key={user.username}
                      onClick={() => setSelectedUser(user)}
                      className={`w-full p-3 flex items-center gap-3 text-left transition-colors ${
                        selectedUser?.username === user.username 
                          ? 'bg-purple-500/20' 
                          : 'hover:bg-gray-100 dark:hover:bg-zinc-800'
                      }`}
                    >
                      {user.photo ? (
                        <img src={user.photo} alt={user.display_name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Users className="h-5 w-5 text-purple-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{user.display_name || user.username}</p>
                        <p className="text-sm text-gray-500 dark:text-zinc-400 truncate">{user.position || user.username}</p>
                      </div>
                      {selectedUser?.username === user.username && (
                        <Check className="h-5 w-5 text-purple-500" />
                      )}
                    </button>
                  ))}
                  {filteredUsers.length === 0 && (
                    <p className="p-4 text-center text-gray-500 dark:text-zinc-400">Aucun utilisateur trouvé</p>
                  )}
                </div>
              </div>
              
              {/* Permissions */}
              {selectedUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-3">
                    Permissions
                  </label>
                  <div className="space-y-2">
                    <PermissionCheckbox
                      label="Modifier les horaires"
                      description="Créer, modifier ou supprimer les horaires d'ouverture et fermeture"
                      checked={permissions.can_edit_schedules}
                      onChange={(e) => setPermissions({ ...permissions, can_edit_schedules: e.target.checked })}
                    />
                    <PermissionCheckbox
                      label="Créer des comptes rendus"
                      description="Accès à l'onglet Compte Rendu pour générer des rapports"
                      checked={permissions.can_create_reports}
                      onChange={(e) => setPermissions({ ...permissions, can_create_reports: e.target.checked })}
                    />
                    <PermissionCheckbox
                      label="Recevoir les alertes email"
                      description="Recevoir les notifications par email en cas d'absence"
                      checked={permissions.can_receive_alerts}
                      onChange={(e) => setPermissions({ ...permissions, can_receive_alerts: e.target.checked })}
                    />
                    <PermissionCheckbox
                      label="Gérer la maintenance"
                      description="Activer ou désactiver le mode maintenance du site"
                      checked={permissions.can_manage_maintenance}
                      onChange={(e) => setPermissions({ ...permissions, can_manage_maintenance: e.target.checked })}
                    />
                    <PermissionCheckbox
                      label="Gérer les administrateurs"
                      description="Ajouter, modifier ou supprimer des administrateurs"
                      checked={permissions.can_manage_admins}
                      onChange={(e) => setPermissions({ ...permissions, can_manage_admins: e.target.checked })}
                    />
                    <PermissionCheckbox
                      label="Gérer les récapitulatifs e-mail"
                      description="Configurer les envois groupés et désactiver les mails individuels"
                      checked={permissions.can_manage_email_digests}
                      onChange={(e) => setPermissions({ ...permissions, can_manage_email_digests: e.target.checked })}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="sticky bottom-0 p-6 border-t flex justify-end gap-3" style={{ 
              background: isDark ? '#1f2937' : '#ffffff',
              borderColor: isDark ? '#374151' : '#e5e7eb' 
            }}>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={addAdmin}
                disabled={!selectedUser}
                className="bg-purple-500 hover:bg-purple-600 text-white"
              >
                Ajouter
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Edit Admin Modal */}
      {showEditModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg rounded-xl shadow-2xl"
            style={{ background: isDark ? '#1f2937' : '#ffffff' }}
          >
            <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
              <div className="flex items-center gap-3">
                {selectedAdmin.photo ? (
                  <img src={selectedAdmin.photo} alt={selectedAdmin.display_name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-purple-500" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">{selectedAdmin.display_name}</h3>
                  <p className="text-sm text-gray-500 dark:text-zinc-400">{selectedAdmin.position || selectedAdmin.username}</p>
                </div>
              </div>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-3">
                Permissions
              </label>
              <div className="space-y-2">
                <PermissionCheckbox
                  label="Modifier les horaires"
                  description="Créer, modifier ou supprimer les horaires d'ouverture et fermeture"
                  checked={permissions.can_edit_schedules}
                  onChange={(e) => setPermissions({ ...permissions, can_edit_schedules: e.target.checked })}
                />
                <PermissionCheckbox
                  label="Créer des comptes rendus"
                  description="Accès à l'onglet Compte Rendu pour générer des rapports"
                  checked={permissions.can_create_reports}
                  onChange={(e) => setPermissions({ ...permissions, can_create_reports: e.target.checked })}
                />
                <PermissionCheckbox
                  label="Recevoir les alertes email"
                  description="Recevoir les notifications par email en cas d'absence"
                  checked={permissions.can_receive_alerts}
                  onChange={(e) => setPermissions({ ...permissions, can_receive_alerts: e.target.checked })}
                />
                <PermissionCheckbox
                  label="Gérer la maintenance"
                  description="Activer ou désactiver le mode maintenance du site"
                  checked={permissions.can_manage_maintenance}
                  onChange={(e) => setPermissions({ ...permissions, can_manage_maintenance: e.target.checked })}
                />
                <PermissionCheckbox
                  label="Gérer les administrateurs"
                  description="Ajouter, modifier ou supprimer des administrateurs"
                  checked={permissions.can_manage_admins}
                  onChange={(e) => setPermissions({ ...permissions, can_manage_admins: e.target.checked })}
                />
                <PermissionCheckbox
                  label="Gérer les récapitulatifs e-mail"
                  description="Configurer les envois groupés et désactiver les mails individuels"
                  checked={permissions.can_manage_email_digests}
                  onChange={(e) => setPermissions({ ...permissions, can_manage_email_digests: e.target.checked })}
                />
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={updateAdmin}
                className="bg-purple-500 hover:bg-purple-600 text-white"
              >
                Enregistrer
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md rounded-xl shadow-2xl p-6"
            style={{ background: isDark ? '#1f2937' : '#ffffff' }}
          >
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Supprimer l'administrateur ?</h3>
              <p className="text-gray-600 dark:text-zinc-400 mb-6">
                Êtes-vous sûr de vouloir supprimer <strong>{selectedAdmin.display_name}</strong> des administrateurs ? Cette action est irréversible.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={deleteAdmin}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  Supprimer
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
}
